// Bench Candidates — data-fetching hooks (ADR-107).
// Same infinite-loading shape as useDirectManagerDiscrepancies: items
// accumulate across loadMore(), reset on filter change, refetch() from the top.

import { useCallback, useEffect, useRef, useState } from 'react';
import { benchCandidatesApi } from '../services/api/benchCandidatesApi';
import { BENCH_CANDIDATE_RUN_STATUSES } from '../constants/benchCandidates';

const emptyPageInfo = { hasNextPage: false, endCursor: null, totalCount: 0 };

// radar-api caps `limit` at 200 per request.
const MAX_SERVER_LIMIT = 200;

/**
 * @param {Object} filters
 * @param {Object} [options]
 * @param {number} [options.restoreCount] - when returning to the page, reload
 *   until at least this many rows are in (so the previous scroll depth exists
 *   again). Consumed once, on the first fetch only.
 */
export const useBenchCandidatesList = (filters = {}, { restoreCount = 0 } = {}) => {
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState(emptyPageInfo);
  const [asOf, setAsOf] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRestoring, setIsRestoring] = useState(restoreCount > 0);
  const [error, setError] = useState(null);

  const filterKey = JSON.stringify(filters);
  const pageInfoRef = useRef(emptyPageInfo);
  pageInfoRef.current = pageInfo;
  const restoreCountRef = useRef(restoreCount);
  // Request sequencing: filters can change faster than the API answers (typing in a
  // text filter, clicking two chips). Only the LATEST request may touch the list —
  // otherwise a slow, older response lands last and the list shows stale rows for
  // the new filters. Every request takes a ticket; a response whose ticket is no
  // longer current is dropped.
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(async (cursor, append = false, limitOverride) => {
    const ticket = requestSeqRef.current + 1;
    requestSeqRef.current = ticket;
    const isCurrent = () => requestSeqRef.current === ticket;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const result = await benchCandidatesApi.list({ ...filters, limit: limitOverride || filters.limit, cursor });
      if (!isCurrent()) {
        return null; // superseded by a newer request
      }
      const nextItems = result?.items || [];
      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setPageInfo(result?.pageInfo || emptyPageInfo);
      setAsOf(result?.asOf || null);
      return { items: nextItems, pageInfo: result?.pageInfo || emptyPageInfo };
    } catch (err) {
      if (!isCurrent()) {
        return null;
      }
      setError(err);
      if (!append) {
        setItems([]);
        setPageInfo(emptyPageInfo);
      }
      return null;
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    let cancelled = false;
    const wanted = restoreCountRef.current;

    const run = async () => {
      try {
        if (!wanted) {
          await fetchPage(undefined, false);
          return;
        }
        // Reload the same depth: first page as large as the server allows, then
        // follow cursors until the previous row count is back (or the list ends).
        setIsRestoring(true);
        let page = await fetchPage(undefined, false, Math.min(Math.max(wanted, filters.limit || 0), MAX_SERVER_LIMIT));
        let loaded = page?.items.length || 0;
        while (!cancelled && page && page.pageInfo.hasNextPage && page.pageInfo.endCursor && loaded < wanted) {
          page = await fetchPage(page.pageInfo.endCursor, true, MAX_SERVER_LIMIT);
          loaded += page?.items.length || 0;
        }
      } finally {
        // One-shot: only a run that was NOT cancelled consumes the restore count,
        // so a later filter change is a normal fresh fetch — while React
        // StrictMode's cancelled first invocation (dev only) leaves it for the
        // second. The flag is cleared on every path so no run can leave the
        // spinner up.
        if (!cancelled) {
          restoreCountRef.current = 0;
        }
        setIsRestoring(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [fetchPage, filters.limit]);

  const loadMore = useCallback(() => {
    const pi = pageInfoRef.current;
    if (pi?.hasNextPage && pi.endCursor) {
      fetchPage(pi.endCursor, true);
    }
  }, [fetchPage]);

  const refetch = useCallback(() => fetchPage(undefined, false), [fetchPage]);

  /** Replace one row in place after an action, without a full refetch. */
  const replaceItem = useCallback((updated) => {
    if (!updated?.id) {
      return;
    }
    setItems((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
  }, []);

  return { items, pageInfo, asOf, isLoading, isLoadingMore, isRestoring, error, loadMore, refetch, replaceItem };
};

/**
 * Latest sync run for a source system. Polls while the run is `running`
 * (after "Refresh now") and calls onSettled once it reaches a terminal state.
 */
export const useLatestBenchCandidateRun = (sourceSystem, { pollMs = 5000, onSettled } = {}) => {
  const [run, setRun] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const previousStatusRef = useRef(null);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const fetchLatest = useCallback(async () => {
    try {
      const latest = await benchCandidatesApi.getLatestRun(sourceSystem);
      setRun(latest);
      setError(null);
      // A run the server flags as stale (snapshot never landed) is not
      // "running" for UI purposes — it must not keep the spinner up or block
      // "Refresh now"; the server expires it on the next trigger.
      const wasRunning = previousStatusRef.current === BENCH_CANDIDATE_RUN_STATUSES.RUNNING;
      const nowRunning = latest?.status === BENCH_CANDIDATE_RUN_STATUSES.RUNNING && !latest?.stale;
      if (wasRunning && !nowRunning && onSettledRef.current) {
        onSettledRef.current(latest);
      }
      previousStatusRef.current = nowRunning ? BENCH_CANDIDATE_RUN_STATUSES.RUNNING : (latest?.status || null);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [sourceSystem]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  const isRunning = run?.status === BENCH_CANDIDATE_RUN_STATUSES.RUNNING && !run?.stale;

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }
    const timer = setInterval(fetchLatest, pollMs);
    return () => clearInterval(timer);
  }, [isRunning, pollMs, fetchLatest]);

  return { run, isRunning, isLoading, error, refetch: fetchLatest };
};

/** Detail (events, import requests, assignPrefill) — loaded on demand for dialogs. */
export const useBenchCandidateDetail = () => {
  const [candidate, setCandidate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (candidateId) => {
    if (!candidateId) {
      setCandidate(null);
      return null;
    }
    // Drop the previous candidate BEFORE the fetch so a dialog opened for a
    // different row never renders (or prefills from) the last one's detail.
    setCandidate(null);
    setIsLoading(true);
    setError(null);
    try {
      const detail = await benchCandidatesApi.get(candidateId);
      setCandidate(detail);
      return detail;
    } catch (err) {
      setError(err);
      setCandidate(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setCandidate(null);
    setError(null);
  }, []);

  return { candidate, isLoading, error, load, clear };
};
